<template>
    <div class="row">
      <div class="col-12">
        <button class="btn btn-primary float-right" @click="toggleNewProgramForm">{{ $t('create-program') }}</button>
        <h1 class="mb-4">{{ $t('programs') }}</h1>
        <Table
            :loading="loading"
            :submitting="submitting"
            :cols="[
                { key: 'name', heading: $t('name'), size: 4, sortable: true,},
                { key: 'displayPublishedState', heading: $t('status'), size: 4 },
                { key: 'courselengthyears', heading: $t('length-(years)'), size: 4 }
            ]"
            :data="filteredPrograms"
            breakpoint="md"
            :filters="{
                ...filters,
                page: filters.page,
                page_size: filters.page_size,
                total_count: total_count,
            }"
            :searchable="true"
            :scrollPaginate="true"
            @refresh="refresh"
            @toPage="toPage"
            @selectItem="x => openEditProgramModal(x)"
            :allowRightClick="true"
            @deleteItem="openDeleteDialog"
        >
            <!-- Table Header -->
            <template v-slot:header>
                <div class="list-group-item align-items-center">
                    <div class="form-inline">
                        <div class="search-filter">
                            <SelectInput
                                v-model="selectedStatus"
                                :options="['active', 'inactive']"
                                :labelCb="(option) => { return $t(option); }"
                                :label="$t('status')"
                                :srOnly="true"
                                :nullable="true"
                                :nullLabel="$t('status')"
                                @input="value => refresh({ status: value })"
                                class="ml-2 mr-2 align-items-center"
                            />
                        </div>
                    </div>

                </div>
            </template>

            <template v-slot:options>
            </template>
        </Table>
  
        <!-- New Program Form Modal -->
        <NewProgramForm
          v-if="showNewProgramForm"
          :showNewProgramForm="showNewProgramForm"
          @save="fetchPrograms"
          @close="toggleNewProgramForm"
          @programCreated="fetchPrograms" 
        />
  
        <!-- Edit Program Form Modal -->
        <EditProgramForm
          v-if="showEditProgramForm"
          :program="selectedProgram"
          @close="closeEditProgramModal"
          @save="fetchPrograms"
        />
      </div>
    </div>
  </template>
  
  <script>
  import Table from "src/components/Table/Table.vue";
  import NewProgramForm from "../../../components/Programmes/NewProgramForm.vue";
  import EditProgramForm from "../../../components/Programmes/EditProgramForm.vue";
  
  export default {
    async mounted() {
      await this.fetchPrograms();
      this.loading = false;
    },
    data() {
      return {
        programs: [],
        filters: { page: 1, page_size: 20 },
        loading: true,
        submitting: false,
        allPrograms: [],
        localSearch: '',
        showNewProgramForm: false,
        showEditProgramForm: false,  
        selectedProgram: null, 
        selectedStatus: null,
        showDeleteButton: {},
        total_count: 0
      };
    },
    computed: {
      filteredPrograms() {
        return this.programs.map(program => ({
          ...program,
          displayPublishedState: Number(program.publishedstate) === 1 ? 'Active' : 'Inactive'
        }));
      }
    },
    methods: {
        async fetchPrograms() {
            if (this.submitting) return;
            this.submitting = true;

            try {
                const res = await this.$cfetch(`${this.$settings.APIBaseURL}/programmes/list`, {
                    method: 'POST',
                    data: this.filters,
                });

                if (res.status !== 'SUCCESS') return;

                this.allPrograms = res.results || [];
                this.programs = this.allPrograms.filter(program => Number(program.publishedstate) !== 3);
            } catch (err) {
                this.$log.error.call(this, err);
            } finally {
                this.submitting = false;
            }
        },
        refresh(filters) {
            this.filters = { ...this.filters, ...filters };
            this.fetchPrograms();
        },
        toPage(pageNo) {
            this.filters.page = pageNo
            this.listUsers()
        },
        toggleNewProgramForm() {
            this.showNewProgramForm = !this.showNewProgramForm;
        },
        openEditProgramModal(program) {
            this.selectedProgram = program; 
            this.showEditProgramForm = true;  
        },
        closeEditProgramModal() {
            this.showEditProgramForm = false; 
            this.selectedProgram = null;  
        },
        filterPrograms() {
            const searchFilter = this.localSearch.toLowerCase();
            const statusFilter = this.selectedStatus === 'all' ? null : Number(this.selectedStatus);
    
            this.programs = this.allPrograms.filter(program => {
            const matchesSearch = program.name.toLowerCase().includes(searchFilter);
            const matchesStatus = 
                statusFilter === null || 
                Number(program.publishedstate) === statusFilter;
            return matchesSearch && matchesStatus;
            });
        },
        handleRightClick(program) {
            const currentVisibility = this.showDeleteButton[program.id];
            this.$set(this.showDeleteButton, program.id, !currentVisibility);
        },
    
        async confirmDelete(program) {
            this.$store.commit("openDialog", {
            cancelBtn: true,
            title: this.$t("delete-topic-question"),
            content: this.$t("delete-topic-dialog"),
            confirmCallBack: async function () {
                this.$store.commit("showBlockingSpinner");
    
                try {
                const res = await this.$cfetch(`${this.$settings.APIBaseURL}/programmes/delete`, {
                    data: { id: program.id, publishedState: 2 }, 
                    method: 'POST',
                });
    
                if (res.status === "SUCCESS") {
                    this.$store.commit("hideBlockingSpinner");
                    this.loaded = false;
                    this.fetchPrograms();
                } else {
                    console.log("Program not deleted");
                }
                } catch (err) {
                console.error("Error during deletion:", err);
                }
            }.bind(this),
            });
        },

        openDeleteDialog(item) {
            this.selectedProgram = item;

            this.$store.commit('openDialog', {
                title: this.$t('are-you-sure'),
                content: this.$t('delete-{e}-warning',{e: this.$t('programme')}),
                cancelBtn: true,
                confirmCallBack: this.removeItem,
            })
        },
        removeItem() {
            if (!this.selectedProgram) return ;
            this.$store.commit("showBlockingSpinner");
            this.$cfetch(`${this.$settings.APIBaseURL}/programmes/delete`, {
                method: 'POST',
                data: { 
                    id: this.selectedProgram.id,
                    publishedState : 3
                }
            }).then(res => {
                this.$store.commit("hideBlockingSpinner");
                if (res.status !== 'SUCCESS') throw new Error('Unexpected error deleting programme')
                this.fetchPrograms();
            }).catch(err => {
                this.$log.error.call(this, err)
                this.$store.commit('openDialog', {
                    title: this.$t('something-went-wrong'),
                    content: this.$t('error-delete-{e}',{e:this.$t('programme')}),
                })
            }).finally(() => {
                this.selectedProgram = undefined
            })
        }
    },
    components: { Table, NewProgramForm, EditProgramForm },
  };
  </script>
  